import useUpdateUserProfile from '@/hooks/user/useUpdateUserProfile';
import { Logger } from '@/utils/logger';
import { isValidPhoneNumber } from '@montugroup/circuit-shared';
import { useUserProfileData } from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import { Button, Select, TextField, toast } from '@montugroup/design-system';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Skeleton, Stack, styled } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';

const Container = styled(Box)(() => ({
  '& .Mui-readOnly:not(.MuiAutocomplete-root input)': {
    backgroundColor: `var(--montu-palette-_components-input-filled-enabledFill)`,
  },
}));

const logger = new Logger('ProfileForm');

type UserProfileFormData = {
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postCode: string;
  state: number | null;
  country: number | null;
};

function ProfileForm() {
  const { data, isLoading } = useUserProfileData();
  const { mutate, isPending } = useUpdateUserProfile();

  const [line1, line2] = (data?.user.address || '').split('$$$$');

  const formMethods = useForm<UserProfileFormData>({
    values: {
      phone: data?.user.phone ?? '',
      addressLine1: line1 ?? '',
      addressLine2: line2 ?? '',
      city: data?.user.city ?? '',
      postCode: data?.user.zip_code ?? '',
      state: data?.user.state_id ?? null,
      country: data?.user.country_id ?? null,
    },
  });

  const {
    register,
    formState: { errors, isDirty },
    control,
    handleSubmit,
    reset,
  } = formMethods;

  const onSubmit = async (formData: UserProfileFormData) => {
    const payload = {
      user: {
        ...data?.user,
        phone: formData.phone,
        address: `${formData.addressLine1}$$$$${formData.addressLine2}`,
        zip_code: formData.postCode,
        city: formData.city,
        country_id: formData.country as number,
        state_id: formData.state as number,
      },
    };
    mutate(payload, {
      onSuccess: () => {
        toast.success('Profile updated successfully!');
        formMethods.reset(formData);
      },
      onError: (error) => {
        toast.error('Something went wrong, please try again.');
        logger.error('Failed to update user profile', error);
      },
    });
  };

  const validatePhone = (phone: string) => {
    return isValidPhoneNumber(phone) || 'Please enter a valid phone number';
  };

  const shouldDisableButtons = !isDirty || isPending || isLoading;

  if (isLoading) {
    return (
      <Stack gap={4} data-testid="loading">
        <Stack direction="row" gap={4}>
          <Skeleton width="100%" height={56} variant="rounded" />
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row" gap={4}>
          <Skeleton width="100%" height={56} variant="rounded" />
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row">
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row">
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row" gap={4}>
          <Skeleton width="100%" height={56} variant="rounded" />
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row" gap={4}>
          <Skeleton width="100%" height={56} variant="rounded" />
          <Skeleton width="100%" height={56} variant="rounded" />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" columnGap={2}>
          <Skeleton width={150} height={38} variant="rounded" />
          <Skeleton width={150} height={38} variant="rounded" />
        </Stack>
      </Stack>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Container>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={4}>
            <Stack direction="row" gap={4}>
              <TextField
                fullWidth
                label="First name"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={data?.user.first_name ?? ''}
              />
              <TextField
                fullWidth
                label="Last name"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={data?.user.last_name ?? ''}
              />
            </Stack>
            <Stack direction="row" gap={4}>
              <TextField
                fullWidth
                label="Email"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={data?.user.email ?? ''}
              />
              <FormControl fullWidth>
                <TextField
                  label="Phone"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.phone}
                  {...register('phone', { required: 'Required', validate: validatePhone })}
                />
                <FormHelperText error={!!errors.phone}>{errors.phone?.message}</FormHelperText>
              </FormControl>
            </Stack>
            <Stack gap={4}>
              <FormControl fullWidth>
                <TextField
                  label="Address"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.addressLine1}
                  {...register('addressLine1', { required: 'Required' })}
                />
                <FormHelperText error={!!errors.addressLine1}>{errors.addressLine1?.message}</FormHelperText>
              </FormControl>
              <TextField
                fullWidth
                label="Address line 2"
                InputLabelProps={{ shrink: true }}
                {...register('addressLine2')}
              />
            </Stack>
            <Stack direction="row" gap={4}>
              <TextField fullWidth label="City" InputLabelProps={{ shrink: true }} {...register('city')} />
              <FormControl fullWidth>
                <InputLabel shrink id="state-label">
                  State
                </InputLabel>
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <Select {...field} error={!!errors.state} labelId="state-label" label="State" displayEmpty>
                      {(data?.states ?? []).map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error={!!errors.state}>{errors.state?.message}</FormHelperText>
              </FormControl>
            </Stack>
            <Stack direction="row" gap={4}>
              <TextField label="Postcode" fullWidth InputLabelProps={{ shrink: true }} {...register('postCode')} />
              <FormControl fullWidth>
                <InputLabel shrink id="country-label">
                  Country
                </InputLabel>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <Select {...field} error={!!errors.country} label="Country" labelId="country-label" displayEmpty>
                      {(data?.countries ?? []).map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error={!!errors.country}>{errors.country?.message}</FormHelperText>
              </FormControl>
            </Stack>
            <Stack direction="row" gap={4} justifyContent="end">
              <Button variant="outlined" disabled={shouldDisableButtons} onClick={() => reset()}>
                Discard changes
              </Button>
              <Button type="submit" variant="contained" disabled={shouldDisableButtons}>
                Update profile
              </Button>
            </Stack>
          </Stack>
        </form>
      </Container>
    </FormProvider>
  );
}

export default ProfileForm;
